import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Layers, Edit2, Trash2, Search, X, Package } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VariantGroupPage() {
  const { user, getAuthHeader } = useAuth();
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [uomOptions, setUomOptions] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',  // Short code for variant code generation
    description: '',
    unit: 'NOS',
    item_ids: []
  });

  const fetchData = async () => {
    try {
      const [groupsRes, itemsRes, uomRes] = await Promise.all([
        axios.get(`${API_URL}/variant-groups`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/items`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/uom/options`, { headers: getAuthHeader() })
      ]);
      setGroups(groupsRes.data);
      // Filter to RMT and SFG items for variants (category field is source of truth)
      setItems(itemsRes.data.filter(i => {
        const cat = i.category?.toUpperCase();
        return cat === 'RMT' || cat === 'SFG';
      }));
      setUomOptions(uomRes.data.codes || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
      // Set fallback UOM options
      if (!uomOptions.length) {
        setUomOptions(['NOS', 'KG', 'MTR', 'SQFT', 'LTR', 'SET']);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a group name');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        abbreviation: formData.abbreviation || formData.name.substring(0, 3).toUpperCase(),  // Default to first 3 chars
        description: formData.description,
        unit: formData.unit,
        item_ids: formData.item_ids
      };

      if (editGroup) {
        await axios.patch(`${API_URL}/variant-groups/${editGroup.id}`, payload, { headers: getAuthHeader() });
        toast.success('Variant group updated successfully');
      } else {
        await axios.post(`${API_URL}/variant-groups`, payload, { headers: getAuthHeader() });
        toast.success('Variant group created successfully');
      }
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save variant group');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      abbreviation: '',
      description: '',
      unit: 'NOS',
      item_ids: []
    });
    setEditGroup(null);
    setSearchTerm('');
    setItemSearchTerm('');
  };

  const handleEdit = (group) => {
    setEditGroup(group);
    setFormData({
      name: group.name,
      abbreviation: group.abbreviation || '',
      description: group.description || '',
      unit: group.unit || 'NOS',
      item_ids: group.item_ids || []
    });
    setOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteGroup) return;
    try {
      await axios.delete(`${API_URL}/variant-groups/${deleteGroup.id}`, { headers: getAuthHeader() });
      toast.success('Variant group deleted');
      setShowDeleteDialog(false);
      setDeleteGroup(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const addItemToGroup = (itemId) => {
    if (!formData.item_ids.includes(itemId)) {
      setFormData({
        ...formData,
        item_ids: [...formData.item_ids, itemId]
      });
    }
    setItemSearchTerm('');
  };

  const removeItemFromGroup = (itemId) => {
    setFormData({
      ...formData,
      item_ids: formData.item_ids.filter(id => id !== itemId)
    });
  };

  // Get item names for display
  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? item.name : 'Unknown Item';
  };

  const getItemDetails = (itemId) => {
    return items.find(i => i.id === itemId);
  };

  // Filter groups by search term
  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter available items for adding (not already in group)
  const availableItems = items.filter(item =>
    !formData.item_ids.includes(item.id) &&
    item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );

  // Get selected items with details
  const selectedItems = formData.item_ids.map(id => getItemDetails(id)).filter(Boolean);

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Please login to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-muted-foreground">
              Group similar items that can be interchanged in a BOM
            </p>
          </div>
          <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Create Variant Group
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editGroup ? 'Edit Variant Group' : 'Create Variant Group'}</DialogTitle>
                <DialogDescription>
                  Group similar items that can be interchanged in a BOM (e.g., different fabric colors)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate}>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Group Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                        placeholder="e.g., FABRIC, POWDER"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Abbreviation *</Label>
                      <Input
                        value={formData.abbreviation}
                        onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value.toUpperCase().substring(0, 5) })}
                        placeholder="e.g., FBR, PWD"
                        maxLength={5}
                        className="uppercase"
                      />
                      <p className="text-xs text-muted-foreground">Used in variant codes (max 5 chars)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Unit *</Label>
                      <select
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        {uomOptions.map(unit => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  {/* Selected Items */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">
                      Selected Items ({formData.item_ids.length})
                    </Label>
                    
                    {selectedItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                        No items selected. Search and add items below.
                      </p>
                    ) : (
                      <div className="border rounded-lg max-h-40 overflow-y-auto">
                        {selectedItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 border-b last:border-b-0 bg-purple-50"
                          >
                            <div className="flex-1">
                              <p className="font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.category.replace('_', ' ')} • {item.unit} • Stock: {item.stock_in_hand || 0}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemFromGroup(item.id)}
                            >
                              <X className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Items Search */}
                  <div className="space-y-3 pt-4 border-t">
                    <Label>Add Items</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={itemSearchTerm}
                        onChange={(e) => setItemSearchTerm(e.target.value)}
                        placeholder="Search items to add..."
                        className="pl-9"
                      />
                    </div>
                    
                    {itemSearchTerm && (
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {availableItems.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No matching items found
                          </p>
                        ) : (
                          availableItems
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer"
                              onClick={() => addItemToGroup(item.id)}
                            >
                              <div className="flex-1">
                                <p className="font-medium">{item.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {item.category.replace('_', ' ')} • {item.unit}
                                </p>
                              </div>
                              <Button type="button" variant="outline" size="sm">
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">
                    {editGroup ? 'Update Variant Group' : 'Save Variant Group'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search variant groups..."
            className="pl-9"
          />
        </div>

        {/* Variant Groups Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Group Name</TableHead>
                <TableHead>Abbreviation</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Unit</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead>Item List</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Layers className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No variant groups found</p>
                    <p className="text-sm text-muted-foreground mt-1">Create a variant group to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded bg-purple-100">
                          <Layers className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="font-medium">{group.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">
                        {group.abbreviation || group.name?.substring(0, 3).toUpperCase() || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {group.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{group.unit || 'NOS'}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                        {group.item_ids?.length || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-md truncate text-sm text-muted-foreground">
                        {group.item_ids?.length > 0 
                          ? group.item_ids.map(id => getItemName(id)).join(', ')
                          : '-'
                        }
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(group)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setDeleteGroup(group); setShowDeleteDialog(true); }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Variant Group</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteGroup?.name}</strong>?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteGroup(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
